import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledH2,
    CheckboxIcon,
    StyledLabel,
    ShieldExclamationIcon,
} from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import moment from "moment";

import { setIsLoading } from "../../store/project";
import { store as userStore } from "../../components/VerticalLayout/store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { publishProjectStatus, RedisPublishList } from "./ModalPublish/hooks";
import {
    getNamesFromKeys,
    parsePaymentErrors,
    showWarningToast,
} from "@helpers/general";
import { errorTypes } from "@constants/index";
import { setKey } from "./store";

type ConfirmationArchiveType = {
    setIsDataUpdated: Function;
    setIsConfirmationPublishProject: Function;
    isConfirmationPublishProject: boolean;
    dataTable: any;
    selectedRows: any;
    dataProject: any;
    setSelectedRows: Function;
};

export function ConfigurationProjectPublish({
    setIsDataUpdated,
    setIsConfirmationPublishProject,
    isConfirmationPublishProject,
    dataTable,
    selectedRows,
    dataProject,
    setSelectedRows,
}: ConfirmationArchiveType): JSX.Element {
    const { t } = useTranslation();
    const { userID, franchiseID, shopID, operatorID, oneShop } = useSnapshot(
        userStore
    );
    const uuidUser = localStorage.getItem("uuidUser");
    const [isDisabled, setIsDisabled] = React.useState(false);

    async function handlePublishProject() {
        let savedData = {
            userId: userID,
            franchiseId: franchiseID,
            shopId: [shopID, "0"],
            projectId: selectedRows.projectId,
            operatorId: operatorID,
            uuidUser: uuidUser,
            status: "Publié",
            modified_at: `${moment().format("DD/MM/YY [at] HH:mm a")}`,
            designation: selectedRows[t("Designation")],
        };

        try {
            const result: any = await publishProjectStatus(savedData);

            const errors = result?.error ?? false;

            let errorMap: any = {};
            if (result.canPublish !== true) {
                errorMap = parsePaymentErrors(result.canPublish);
            }

            const toastConfig: any = {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                newestOnTop: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                icon: (
                    <ShieldExclamationIcon
                        width={25}
                        height={25}
                        fill="white"
                    />
                ),
            };

            if (
                (result.canPublish !== true &&
                    result.canPublish.some((el: any) =>
                        Object.values(el).includes("hasActiveButMissingData")
                    )) ||
                result.hasActiveMeansOfPayment === false
            ) {
                if (result.hasActiveMeansOfPayment === false) {
                    toast.error(
                        t(
                            "Please note, you cannot publish a template without an activated payment method"
                        ),
                        toastConfig
                    );
                }
                const targetErrorType = "hasActiveButMissingData";

                if (errorTypes.includes(targetErrorType)) {
                    const errorType = targetErrorType;
                    const keys = errorMap[errorType];

                    const { names, labels } = getNamesFromKeys(keys);

                    const messageMap: Record<ErrorType, string> = {
                        hasActiveButMissingData: `Attention, one or more kiosk applications do not have an associated {{names}}.`,
                    };

                    toast.error(
                        t(messageMap[errorType], {
                            names: names,
                            labels: labels,
                        }),
                        toastConfig
                    );
                }
            } else if (result.message === 3) {
                toast.info(
                    `${t(
                        "Warning: This template is currently being edited by another user. Please try again later."
                    )!}`,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );
            } else if (!errors) {
                toast.success(
                    `${t("The project is published successfully")!}`,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );

                setKey(Math.floor(Math.random() * 10000));
                let ids: any = (!oneShop
                    ? [...selectedRows.subRows]
                    : [selectedRows]
                ).map((el: any) => el.shopId);
                RedisPublishList(ids);
            }
            if (result.canPublish !== true) {
                showWarningToast(result, toastConfig);
            }
        } catch (e: any) {
            console.log(`${t("There's an error")}!`);
        }
    }

    return (
        <StyledModal
            toggle={true}
            isOpen={isConfirmationPublishProject}
            centered
        >
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsConfirmationPublishProject(
                        !isConfirmationPublishProject
                    );
                    setSelectedRows([]);
                }}
            >
                <StyledH2 className="text-uppercase">{t("Alert")}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {`${t(
                        "Attention, a Template is already published.The publication of the"
                    )}''${selectedRows.Désignation}''${t(
                        "Template generates the dissociation of the shops of the Template published.\n Do you want to publish the selected Template?"
                    )}`}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsConfirmationPublishProject(
                            !isConfirmationPublishProject
                        );
                        setSelectedRows([]);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    disabled={isDisabled}
                    onClick={async () => {
                        setIsDisabled(true);
                        setIsConfirmationPublishProject(false);
                        setIsLoading(true);
                        await handlePublishProject();
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
